// GDPR API client — Phase 5 Plan 05-03 (GDP-04/05).
//
// Wraps token-bound public endpoints:
//   POST /api/v1/gdpr/confirm-delete?token=<plaintext>
//   POST /api/v1/gdpr/cancel-delete?token=<plaintext>
//
// These endpoints are PUBLIC (no Bearer JWT) — the token in the URL is the
// credential. See backend api/routers/gdpr.py docstring.
//
// Mini App ConfirmDelete static landing renders one of 5 UI states based on
// the API response status:
//   200 → confirmed
//   404 → invalid
//   410 → expired
//   409 → already_executed (or cancelled)
//   other → network_error

export type GdprConfirmStatus =
  | "pending"
  | "confirmed"
  | "expired"
  | "invalid"
  | "already_executed"
  | "network_error"
  // W-009: explicit signal that the request was aborted by the caller
  // (e.g., component unmount). UI must NOT render network_error для
  // aborted requests — компонент уже размонтирован, юзеру нечего показать.
  | "aborted";

export interface ConfirmDeleteResponse {
  status: "confirmed";
  scheduled_for: string;
}

export interface CancelDeleteResponse {
  status: "cancelled";
  cancelled_at: string;
}

export interface GdprConfirmResult {
  status: GdprConfirmStatus;
  scheduled_for?: string;
  cancelled_at?: string;
  detail?: string;
}

/**
 * Resolve {baseUrl}/api/v1/gdpr/{path}?token={token} → POST.
 *
 * No Authorization header — token is the credential.
 */
async function _postToken(
  baseUrl: string,
  path: string,
  token: string,
  signal?: AbortSignal,
): Promise<Response> {
  const url = `${baseUrl.replace(/\/$/, "")}/api/v1/gdpr/${path}?token=${encodeURIComponent(token)}`;
  return fetch(url, {
    method: "POST",
    headers: { Accept: "application/json" },
    signal,
  });
}

/**
 * POST /api/v1/gdpr/confirm-delete?token=<plaintext>
 *
 * Maps HTTP status → GdprConfirmStatus enum for UI rendering.
 */
export async function confirmDelete(
  baseUrl: string,
  token: string,
  signal?: AbortSignal,
): Promise<GdprConfirmResult> {
  try {
    const res = await _postToken(baseUrl, "confirm-delete", token, signal);
    if (res.status === 200) {
      const body = (await res.json()) as ConfirmDeleteResponse;
      return { status: "confirmed", scheduled_for: body.scheduled_for };
    }
    if (res.status === 404) {
      return { status: "invalid" };
    }
    if (res.status === 410) {
      return { status: "expired" };
    }
    if (res.status === 409) {
      return { status: "already_executed" };
    }
    return { status: "network_error", detail: `HTTP ${res.status}` };
  } catch (err) {
    // W-009: detect AbortError BEFORE generic network_error. Aborts
    // происходят при unmount компонента — реальный POST мог пройти,
    // и показывать "network error" юзеру некорректно (плюс компонент
    // уже размонтирован).
    if (err instanceof Error && err.name === "AbortError") {
      return { status: "aborted" };
    }
    return {
      status: "network_error",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * POST /api/v1/gdpr/cancel-delete?token=<plaintext>
 */
export async function cancelDelete(
  baseUrl: string,
  token: string,
  signal?: AbortSignal,
): Promise<GdprConfirmResult> {
  try {
    const res = await _postToken(baseUrl, "cancel-delete", token, signal);
    if (res.status === 200) {
      const body = (await res.json()) as CancelDeleteResponse;
      return { status: "confirmed", cancelled_at: body.cancelled_at };
    }
    if (res.status === 404) {
      return { status: "invalid" };
    }
    if (res.status === 409) {
      return { status: "already_executed" };
    }
    return { status: "network_error", detail: `HTTP ${res.status}` };
  } catch (err) {
    // W-009: см. confirmDelete выше.
    if (err instanceof Error && err.name === "AbortError") {
      return { status: "aborted" };
    }
    return {
      status: "network_error",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

export const gdprApi = {
  confirmDelete,
  cancelDelete,
};
